import React, { useContext } from 'react';
import { MainContext } from '../../context/MainContext';

import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';

import PowerOutlinedIcon from '@mui/icons-material/PowerOutlined';
import AcUnitOutlinedIcon from '@mui/icons-material/AcUnitOutlined';
import WhatshotOutlinedIcon from '@mui/icons-material/WhatshotOutlined';
import DeviceThermostatOutlinedIcon from '@mui/icons-material/DeviceThermostatOutlined';
import ToggleOnOutlinedIcon from '@mui/icons-material/ToggleOnOutlined';
import ToggleOffOutlinedIcon from '@mui/icons-material/ToggleOffOutlined';

function createRow(id, label) {
  return { id, label };
}

const ICON_COLORS = {
  heat: "#FF7043",        // orange / heat
  cool: "#4FC3F7",        // light blue
  water: "#29B6F6",       // pool / water
  time: "#BA68C8",        // timers
  power: "#FFD54F",       // electrical
  speed: "#81C784",       // motion / speed
  level: "#90A4AE",       // neutral / measurement
  error: "#FF0000",
  bluetooth: "#0a3d91",
  wifi: "#D3D3D3",
  mqtt: "#660066"
};

const RELAY_ICONS = {
  Pump: <PowerOutlinedIcon sx={{ color: ICON_COLORS.time }} />,
  Fan: <AcUnitOutlinedIcon sx={{ color: ICON_COLORS.cool }} />,
  '4-Way Valve': <DeviceThermostatOutlinedIcon sx={{ color: ICON_COLORS.water }} />,
  Compressor: <WhatshotOutlinedIcon sx={{ color: ICON_COLORS.heat }} />,
  'EEV Boost': <ToggleOnOutlinedIcon />,
  EVI: <ToggleOnOutlinedIcon />,
  'Element Condenser': <DeviceThermostatOutlinedIcon sx={{ color: ICON_COLORS.water }} />,
  'Element Compressor': <WhatshotOutlinedIcon sx={{ color: ICON_COLORS.heat }} />,
};

export default function HPDashboardRelays() {
  const state = useContext(MainContext).state;
  const re = state.RE?.split(',') ?? [];

  const rows = [
    createRow(0, 'Pump'),
    createRow(1, 'Fan'),
    createRow(2, '4-Way Valve'),
    createRow(3, 'Compressor'),
    createRow(4, 'EEV Boost'),
    createRow(5, 'EVI'),
    createRow(6, 'Element Condenser'),
    createRow(7, 'Element Compressor'),
  ];

  // Default assignments
  const values = Array(rows.length).fill('OFF');

  const set = (index, condition) => {
    values[index] = condition ? 'ON' : 'OFF';
  };

  // Base mapping
  set(1, re[13] === '1');
  set(2, re[14] === '1');
  set(3, re[12] === '1');
  set(6, re[15] === '1');

  if (re[1] === '2') {
    set(0, re[17] === '1');
    set(4, re[21] === '1');
    set(5, re[22] === '1');
    set(7, re[20] === '1');

    set(1, re[16] === '1');
    set(2, re[18] === '1');
    set(3, re[15] === '1');
    set(6, re[19] === '1');
  }

  return (
    <>
      <Typography
        component="h2"
        variant="h6"
        color="white"
        sx={{ fontWeight: 600, mb: 2 }}
      >
        Relays
      </Typography>

      <Grid container spacing={1.5}>
        {rows.map((row, index) => {
          const value = values[index];
          const isOn = value === 'ON';
          const icon = RELAY_ICONS[row.label] || <ToggleOffOutlinedIcon />;

          const card = (
            <Paper
              sx={{
                p: 1.2,
                height: 60,
                display: 'flex',
                alignItems: 'center',
                gap: 1.2,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.1)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.08)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(255,255,255,0.12)', // icon always grey
                  color: 'rgba(255,255,255,0.7)',
                  flexShrink: 0,
                }}
              >
                {icon}
              </Box>

              <Box
                sx={{
                  flexGrow: 1,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  gap: 0.3,
                  mt: 0.2,
                }}
              >
                <Typography
                  color="white"
                  sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.1 }}
                  noWrap
                >
                  {row.label}
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    fontSize: 13,
                    color: isOn ? 'success.main' : 'text.disabled',
                  }}
                >
                  {value}
                </Typography>
              </Box>
            </Paper>
          );

          return (
            <Grid item xs={12} key={row.id}>
              <Tooltip title={row.label} arrow placement="top">
                <Box>{card}</Box>
              </Tooltip>
            </Grid>
          );
        })}
      </Grid>
    </>
  );
}
